'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import MediaPlayer from '../../../../components/ui/MediaPlayer/MediaPlayer';
import ModalBase from '../../../../components/ui/ModalBase/ModalBase';
import WChanceStaging from '../../../../components/ui/WChanceStaging/WChanceStaging';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { isVersion } from '../../../../utils/version';
import { DELIVERY_STATUS, LOTTERY_DOWNLOAD_STATUS, LotteryPrizeType } from '../../constants';
import type {
  LotteryDeliveryDetailCollection,
  LotteryEventDetailCollection,
  LotteryGoodsCollectionStagingInfo,
  LotteryGoodsCustomFormItem,
  LotteryGoodsDetailCollection,
  LotteryPrizeTypeType,
} from '../../types';
import { CustomFormType } from '../../types';
import { createStagingInfo } from '../../utils';
import styles from './LotteryCollectionDetail.module.scss';

export type LotteryCollectionDetailProps = {
  event: LotteryEventDetailCollection;
  goods: LotteryGoodsDetailCollection;
  delivery: LotteryDeliveryDetailCollection;
  onDownloadCollection: (id: number) => Promise<void>;
  onEditOption: (goodsId: number, deliveryId: number) => void;
  onOptionInputClick: () => void;
  iconDownloadSrc: string;
  isDisplayPrizeDownloadHelpForApp?: boolean;
  hidePrizeLabel?: boolean;
  copyToClipboard?: () => void;
  serviceBaseUrl: string;
  showProhibitAnnotation?: boolean;
};

function LotteryCollectionDetail({
  event,
  goods,
  delivery,
  onDownloadCollection,
  onEditOption,
  onOptionInputClick,
  iconDownloadSrc,
  isDisplayPrizeDownloadHelpForApp,
  hidePrizeLabel = false,
  serviceBaseUrl,
  showProhibitAnnotation = false,
}: LotteryCollectionDetailProps) {
  const stagingInfo = createStagingInfo(goods.stagingKey);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const downloadStatus = goods.downloadStatus;

  const downloadCollection = async () => {
    if (downloadStatus !== LOTTERY_DOWNLOAD_STATUS.available.value || isLoading) return;
    setIsLoading(true);
    try {
      await onDownloadCollection(goods.id);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${serviceBaseUrl}/account/collection`);
      alert('URLをコピーしました');
    } catch {
      alert('コピーに失敗しました');
    }
  };

  const router = useRouter();

  const shouldShowOptionInputButton = (): boolean => {
    if (goods.isV2OptionCompleted === null) return false;
    if (goods.isV2OptionCompleted !== false) return false;
    if (!isVersion(event.version, '2.0')) return false;
    return true;
  };

  const shouldShowOptionViewButton = (): boolean => {
    if (goods.isV2OptionCompleted === null) return false;
    if (goods.isV2OptionCompleted !== true) return false;
    if (!isVersion(event.version, '2.0')) return false;
    return true;
  };

  return (
    <div className={styles.lotteryCollectionDetail}>
      {/*　特典と演出の出し分け */}
      {!hidePrizeLabel && <div className={styles.rankWrapper}>{staging(goods, stagingInfo)}</div>}
      {/*　景品名 */}
      <p className={`${styles.itemTitle} ${utilsStyles.textBold} ${utilsStyles.mtXSmall}`}>{goods.prizeLabelName}</p>
      <p className={`${styles.itemGoodsName} ${utilsStyles.textBold}`}>{goods.name}</p>
      <div className={utilsStyles.mtSmall}>
        <hr className={utilsStyles.pinkishgrayBgColor} />
      </div>
      {/*　景品ラベルの説明 */}
      <div className={utilsStyles.mtSmall}>
        <p className={`${utilsStyles.textSmall}`}>{goods.prizeLabelDescription}</p>
      </div>
      {/* オプション */}
      {goods.prizeOptionFlag !== 0 && !isVersion(event.version, '2.0') && (
        <div className={`${utilsStyles.mtSmall} ${styles.itemOption}`}>
          <div>
            <p className={`${utilsStyles.textBold} ${utilsStyles.textSmall}`}>オプションの入力内容</p>
            {currentOptionInput(goods.customForms, delivery.option)}
          </div>
          <button
            type='button'
            className={styles.editButton}
            onClick={() => {
              onEditOption(goods.id, delivery.id);
            }}
          >
            編集
          </button>
        </div>
      )}
      {/* 景品がメディアであればモーダルと表示ボタン設置 */}
      {isMediaPrize(goods.prizeLabelType) && (
        <>
          <div className={`${utilsStyles.mtSmall} ${utilsStyles.centered}`}>
            <ActionButton
              color={downloadStatus === LOTTERY_DOWNLOAD_STATUS.preparing.value ? 'warmgray' : undefined}
              disabled={downloadStatus === LOTTERY_DOWNLOAD_STATUS.preparing.value}
              onClick={() => setIsOpen(true)}
            >
              {showButton(goods.prizeLabelType)}
            </ActionButton>
          </div>
          {downloadStatus !== LOTTERY_DOWNLOAD_STATUS.disabled.value && (
            <div className={`${utilsStyles.mtSmall} ${utilsStyles.centered}`}>
              <ActionButton
                color={downloadStatus === LOTTERY_DOWNLOAD_STATUS.available.value ? 'primary' : 'warmgray'}
                disabled={
                  downloadStatus === LOTTERY_DOWNLOAD_STATUS.pending.value ||
                  downloadStatus === LOTTERY_DOWNLOAD_STATUS.preparing.value ||
                  isLoading
                }
                isLoading={isLoading}
                onClick={downloadCollection}
              >
                {showDownloadButton(goods.prizeLabelType, iconDownloadSrc)}
              </ActionButton>
              {downloadStatus === LOTTERY_DOWNLOAD_STATUS.pending.value && (
                <p className={styles.downloadNotice}>※くじ終了後にダウンロード可能になります。</p>
              )}
              {downloadStatus === LOTTERY_DOWNLOAD_STATUS.preparing.value && (
                <p className={styles.downloadNotice}>
                  ※{LotteryPrizeType[goods.prizeLabelType].text}
                  は準備中です。後日公開時にご案内いたしますので、今しばらくお待ちください。
                </p>
              )}
            </div>
          )}
          {/* モーダル */}
          <ModalBase isOpen={isOpen} onClose={() => setIsOpen(false)} bgColor={'transparent'}>
            {modal(goods.prizeLabelType, goods)}
          </ModalBase>
        </>
      )}
      {/* 景品テキスト */}
      {showPrizeText(goods.prizeLabelType, goods.prizeText) && (
        <div className={`${utilsStyles.mtSmall} ${styles.itemPrizeText}`}>
          <p className={`${utilsStyles.textSmall} ${utilsStyles.textPrewrap}`}>{goods.prizeText}</p>
        </div>
      )}
      {/* オプション入力ボタン */}
      {shouldShowOptionInputButton() && (
        <div className={`${utilsStyles.mtSmall} ${styles.optionButtonContainer}`}>
          <ActionButton onClick={onOptionInputClick} size='large' color='primary'>
            オプション入力をする
          </ActionButton>
        </div>
      )}
      {/* 入力済みオプション確認 */}
      {shouldShowOptionViewButton() && (
        <div className={`${utilsStyles.mtSmall} ${styles.optionButtonContainer}`}>
          <button
            type='button'
            className={styles.optionButton}
            onClick={() =>
              router.push(
                `/account/collection/option?lotteryPrizeGoodsId=${goods.id}&lotteryCutoffId=${event.cutoffId}`,
              )
            }
          >
            入力済みのオプションを確認する
          </button>
        </div>
      )}
      {/* ステータス　*/}
      {DELIVERY_STATUS[delivery.status] && (
        <div className={utilsStyles.mtSmall}>
          <p className={`${utilsStyles.textLarge} ${utilsStyles.textCenter}`}>
            配送ステータス：
            <strong style={{ color: DELIVERY_STATUS[delivery.status].color }}>
              {DELIVERY_STATUS[delivery.status].text}
            </strong>
          </p>
        </div>
      )}
      {/*ダウンロードヘルプ*/}
      {isDisplayPrizeDownloadHelpForApp && isMediaPrize(goods.prizeLabelType) && (
        <div className={utilsStyles.mtSmall}>
          <div className={utilsStyles.textXSmall}>
            <p className={styles.downloadHelpText}>
              景品がダウンロードできない場合は、画像を長押しして保存してください。
              <br />
              それでも保存できない場合は、お手数ですが以下のURLをブラウザで開き、「獲得した景品一覧」からダウンロードをお試しください。
              <br />
            </p>
            <button type='button' onClick={copyToClipboard} className={styles.copyButton}>
              URL : {serviceBaseUrl}/account/collection
            </button>
            <p>ブラウザのアドレスバーに上記のURLをコピー＆ペーストしてアクセスしてください。</p>
          </div>
        </div>
      )}
      {/* 配送説明 */}
      {event.shippingDescription && (
        <div className={utilsStyles.mtSmall}>
          <div
            className={`${utilsStyles.textXSmall} ${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{ __html: event.shippingDescription }}
          />
          {/* ゼロイチとグッスマは非表示、他のOEMは表示する注意事項 */}
          {/* 呼び出し側でフラグを渡して分岐 */}
          {showProhibitAnnotation && (
            <p className={utilsStyles.textXSmall}>
              ・獲得したデジタルコンテンツのSNSなどへの無断公開・譲渡など著作権侵害行為は禁止です。
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function isMediaPrize(prizeLabelType: LotteryPrizeTypeType) {
  return (
    prizeLabelType === LotteryPrizeType.image.value ||
    prizeLabelType === LotteryPrizeType.video.value ||
    prizeLabelType === LotteryPrizeType.audio.value
  );
}

function showPrizeText(prizeLabelType: LotteryPrizeTypeType, prizeText?: string | null): boolean {
  return prizeLabelType === LotteryPrizeType.text.value && prizeText !== null;
}

function showButtonTextSuffix(type: LotteryPrizeTypeType) {
  if (type === LotteryPrizeType.image.value) return 'を表示する';
  if (type === LotteryPrizeType.video.value || type === LotteryPrizeType.audio.value) return 'を再生する';
  return '';
}

function showButton(type: LotteryPrizeTypeType) {
  return (
    <div className={`${utilsStyles.textLarge} ${utilsStyles.centered}`}>
      <img src={LotteryPrizeType[type].src} alt='icon' className={utilsStyles.textIcon} />
      &nbsp;{LotteryPrizeType[type].text}
      {showButtonTextSuffix(type)}
    </div>
  );
}

function showDownloadButton(type: LotteryPrizeTypeType, iconDownloadSrc: string) {
  return (
    <div className={`${utilsStyles.textLarge} ${utilsStyles.centered}`}>
      <img src={iconDownloadSrc} alt='icon' className={utilsStyles.textIcon} />
      &nbsp;{LotteryPrizeType[type].text}をダウンロードする
    </div>
  );
}

function modal(type: LotteryPrizeTypeType, goods: LotteryGoodsDetailCollection) {
  const fileUrl = goods.fileUrl;
  switch (type) {
    case LotteryPrizeType.image.value:
      return <ImageView src={fileUrl} alt='' isFreeSize={true} />;
    case LotteryPrizeType.video.value:
      return <MediaPlayer src={fileUrl} tag={'video'} />;
    case LotteryPrizeType.audio.value:
      return <MediaPlayer src={fileUrl} tag={'audio'} />;
    default:
      return <></>;
  }
}

function getRankStyle(rank: number) {
  if (rank === 1) {
    return styles.rank1;
  }
  if (rank === 2) {
    return styles.rank2;
  }
  if (rank === 3) {
    return styles.rank3;
  }
  return '';
}

function staging(goods: LotteryGoodsDetailCollection, stagingInfo: LotteryGoodsCollectionStagingInfo) {
  if (goods.isRepostCampaign) {
    // リポストキャンペーン特典
    return (
      <div className={styles.repostWrapper}>
        <span className={fontsStyles.montserrat}>リポストキャンペーン</span>
      </div>
    );
  }
  if (goods.benefitMultiDrawCount > 0) {
    // 特典
    return (
      <div className={styles.benefitWrapper}>
        <span className={fontsStyles.montserrat}>{goods.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }
  if (goods.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'large'} />;
  }
  if (stagingInfo.stagingRank) {
    // 演出
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

function customOptionValue(form: LotteryGoodsCustomFormItem) {
  if (form.type === CustomFormType.checkbox.value) {
    const values = form.value as string[];
    if (values?.length === 0 || !values) return null;

    const result: string[] = [];
    values.forEach((value) => {
      const selectedOption = form.options?.find((option) => String(option.value) === value);
      if (selectedOption) {
        result.push(selectedOption.label);
      }
    });
    return result.join(', ');
  }

  if (form.type === CustomFormType.radio.value) {
    const value = form.value as string;
    if (!value) return null;

    const selectedOption = form.options?.find((option) => String(option.value) === value);
    if (selectedOption) {
      return selectedOption.label;
    }
  }

  return form.value;
}

function currentOptionInput(customForms: LotteryGoodsCustomFormItem[], note?: string) {
  if (customForms.length > 0) {
    return (
      <div className={`${utilsStyles.textSmall} ${utilsStyles.textPrewrap}`}>
        {customForms.map((form) => (
          <div key={form.hash} className={utilsStyles.mtXSmall}>
            <p className={utilsStyles.textBold}>{form.label}</p>
            <p>{customOptionValue(form) ?? '入力されていません'}</p>
          </div>
        ))}
      </div>
    );
  }
  return <p className={`${utilsStyles.textSmall} ${utilsStyles.textPrewrap}`}>{note ?? '入力されていません'}</p>;
}

export default LotteryCollectionDetail;
